import React from 'react';
import { motion } from 'framer-motion';

const Disclaimer = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  return (
    <div className="pt-16 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <motion.div {...fadeInUp}>
          <h1 className="text-4xl font-bold text-[#0B1320] font-poppins mb-8">
            Disclaimer
          </h1>
          
          <div className="prose prose-lg max-w-none text-[#4A4A4A] leading-relaxed space-y-8">
            <div>
              <p className="text-sm text-[#9E9E9E] mb-6">
                Last updated: January 2025
              </p>
              
              <div className="bg-red-50 border border-red-200 p-6 rounded-lg mb-8">
                <p className="font-bold text-red-800 mb-4">
                  IMPORTANT: PLEASE READ THIS DISCLAIMER CAREFULLY BEFORE USING OUR SERVICES
                </p>
                <p className="text-red-800">
                  The information provided by Sam's Nutri Hub is for educational and informational purposes only. 
                  We are not medical doctors and do not provide medical diagnosis, treatment, or cure for any disease or condition.
                </p>
              </div>
            </div>

            <section>
              <h2 className="text-2xl font-bold text-[#0B1320] font-poppins mb-4">
                General Health Information Disclaimer
              </h2>
              <p className="mb-4">
                The content on our website, consultations, and programs is provided for general informational purposes only. 
                It is not intended as a substitute for professional medical advice, diagnosis, or treatment. 
                The information should not be considered complete and should not be used in place of a visit, call, 
                consultation, or advice of your physician or other healthcare provider.
              </p>
              
              <div className="bg-yellow-50 border border-yellow-200 p-6 rounded-lg mb-6">
                <h3 className="font-semibold text-yellow-800 mb-2">Always Consult Your Healthcare Provider</h3>
                <ul className="list-disc pl-6 space-y-2 text-yellow-800">
                  <li>Before starting any new nutrition or exercise program</li>
                  <li>If you have any medical conditions or concerns</li>
                  <li>If you are taking any medications</li>
                  <li>If you experience any adverse reactions</li>
                  <li>For any questions about your specific health situation</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#0B1320] font-poppins mb-4">
                No Medical Practice
              </h2>
              <p className="mb-4">
                Sam's Nutri Hub and its team members are nutrition and wellness coaches, not licensed medical practitioners. 
                We do not:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-6">
                <li>Diagnose, treat, cure, or prevent any disease</li>
                <li>Prescribe medications or medical treatments</li>
                <li>Provide medical advice or medical opinions</li>
                <li>Replace the need for professional medical care</li>
                <li>Practice medicine in any form</li>
                <li>Make medical claims about our services</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#0B1320] font-poppins mb-4">
                Individual Results Disclaimer
              </h2>
              <div className="bg-blue-50 border border-blue-200 p-6 rounded-lg mb-6">
                <h3 className="font-semibold text-blue-800 mb-2">Results May Vary</h3>
                <ul className="list-disc pl-6 space-y-2 text-blue-800">
                  <li>Individual results may vary significantly</li>
                  <li>Success depends on many factors including compliance, genetics, and lifestyle</li>
                  <li>Testimonials and case studies represent individual experiences</li>
                  <li>Past results do not guarantee future outcomes</li>
                  <li>No specific results are promised or guaranteed</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#0B1320] font-poppins mb-4">
                Nutritional Guidance Disclaimer
              </h2>
              <p className="mb-4">
                Our nutritional recommendations and meal plans are based on general nutritional principles 
                and your provided information. However:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-6">
                <li>We cannot account for all individual medical conditions or restrictions</li>
                <li>Food allergies and intolerances must be communicated clearly</li>
                <li>Dietary changes should be made gradually and with awareness</li>
                <li>Some individuals may have adverse reactions to certain foods or supplements</li>
                <li>Nutritional needs vary based on age, health status, and activity level</li>
                <li>Professional medical guidance may be necessary for specific conditions</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#0B1320] font-poppins mb-4">
                Exercise and Physical Activity Disclaimer
              </h2>
              <div className="bg-orange-50 border border-orange-200 p-6 rounded-lg mb-6">
                <h3 className="font-semibold text-orange-800 mb-2">Exercise Risks</h3>
                <p className="text-orange-800 mb-2">
                  Physical exercise and activity involve inherent risks. Before beginning any exercise program:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-orange-800">
                  <li>Consult with your healthcare provider</li>
                  <li>Ensure you are physically capable of the activities</li>
                  <li>Start slowly and progress gradually</li>
                  <li>Stop immediately if you experience pain or discomfort</li>
                  <li>Understand your physical limitations</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#0B1320] font-poppins mb-4">
                Supplement and Product Disclaimer
              </h2>
              <p className="mb-4">
                When we recommend supplements or products:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-6">
                <li>These recommendations are for educational purposes only</li>
                <li>We are not responsible for product quality, safety, or efficacy</li>
                <li>Always read product labels and follow manufacturer instructions</li>
                <li>Consult healthcare providers before starting any supplements</li>
                <li>Be aware of potential interactions with medications</li>
                <li>Discontinue use if adverse reactions occur</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#0B1320] font-poppins mb-4">
                Technology and Platform Disclaimer
              </h2>
              <p className="mb-4">
                Regarding our website, apps, and digital platforms:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-6">
                <li>Information is provided "as is" without warranties</li>
                <li>Technical issues may affect service availability</li>
                <li>We are not responsible for third-party integrations or links</li>
                <li>Data accuracy depends on user input</li>
                <li>Platform features may change without notice</li>
                <li>Internet connectivity issues may affect service quality</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#0B1320] font-poppins mb-4">
                Professional Relationship Disclaimer
              </h2>
              <p className="mb-6">
                Our services create a coaching relationship, not a doctor-patient relationship. 
                We provide education, guidance, and support based on nutrition and wellness principles, 
                but we do not provide medical care or treatment. You remain responsible for your own 
                health decisions and should maintain regular contact with appropriate healthcare providers.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#0B1320] font-poppins mb-4">
                Liability Limitations
              </h2>
              <div className="bg-gray-50 border border-gray-200 p-6 rounded-lg mb-6">
                <h3 className="font-semibold text-gray-800 mb-2">Use at Your Own Risk</h3>
                <p className="text-gray-800 mb-2">
                  By using our services, you acknowledge that:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-800">
                  <li>You use our services at your own risk</li>
                  <li>We are not liable for any health outcomes or consequences</li>
                  <li>You are responsible for your own health and safety</li>
                  <li>We cannot guarantee any specific results</li>
                  <li>You will seek appropriate medical care when needed</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#0B1320] font-poppins mb-4">
                Emergency Situations
              </h2>
              <div className="bg-red-50 border border-red-200 p-6 rounded-lg mb-6">
                <h3 className="font-semibold text-red-800 mb-2">IMPORTANT - Medical Emergencies</h3>
                <p className="text-red-800 mb-2">
                  Our services are not for emergency situations. In case of medical emergency:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-red-800">
                  <li>Call your local emergency number (108 in India) immediately</li>
                  <li>Contact your healthcare provider or go to the nearest hospital</li>
                  <li>Do not rely on our services for urgent medical needs</li>
                  <li>Do not delay seeking emergency medical care</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#0B1320] font-poppins mb-4">
                Accuracy of Information
              </h2>
              <p className="mb-6">
                While we strive to provide accurate and up-to-date information, we cannot guarantee 
                the completeness, accuracy, or reliability of any content. Information may become 
                outdated, and new research may change current understanding. Always verify information 
                with qualified healthcare professionals and current medical literature.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#0B1320] font-poppins mb-4">
                Third-Party Content and Links
              </h2>
              <p className="mb-6">
                Our website may contain links to third-party websites or content. We are not responsible 
                for the accuracy, completeness, or reliability of any third-party information. 
                These links are provided for convenience only and do not constitute endorsement.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#0B1320] font-poppins mb-4">
                Regulatory Compliance
              </h2>
              <p className="mb-6">
                Our services comply with applicable laws and regulations in India. However, regulations 
                may vary by location, and it is your responsibility to ensure compliance with local laws 
                regarding nutrition counseling and wellness services in your jurisdiction.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#0B1320] font-poppins mb-4">
                Updates to This Disclaimer
              </h2>
              <p className="mb-6">
                We may update this disclaimer periodically to reflect changes in our services, 
                legal requirements, or best practices. Please review this disclaimer regularly. 
                Continued use of our services after changes indicates acceptance of the updated disclaimer.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#0B1320] font-poppins mb-4">
                Contact Information
              </h2>
              <p className="mb-4">
                If you have questions about this disclaimer or our services, please contact us:
              </p>
              <div className="bg-[#F9FFF5] p-6 rounded-lg">
                <p><strong>Sam's Nutri Hub</strong></p>
                <p>Venkatesh Prime, 4th Floor</p>
                <p>Burudgaon Road, Ahmednagar, Maharashtra 414001</p>
                <p>Email: info@samsnutrihub.com</p>
                <p>Phone: +91 98765 43210</p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#0B1320] font-poppins mb-4">
                Acknowledgment and Acceptance
              </h2>
              <div className="bg-green-50 border border-green-200 p-6 rounded-lg">
                <p className="font-semibold text-green-800 mb-2">
                  By using our services, you acknowledge that:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-green-800">
                  <li>You have read and understood this disclaimer</li>
                  <li>You accept the limitations and conditions outlined</li>
                  <li>You will use our services responsibly and safely</li>
                  <li>You will seek appropriate medical care when needed</li>
                  <li>You understand we are not medical practitioners</li>
                </ul>
              </div>
            </section>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Disclaimer;